import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import { MapPin, PawPrint, Megaphone, Wrench, X, ArrowRight, ChevronDown, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { resourceCategories as mainCategories, iconMap } from '../../data/resourceCategories';
import searchExpandedCardData from '../../pages/Search/searchExpandedCardData';
import ExpandedCard from '../AnimalHelpGuide/ExpandedCard/ExpandedCard';
import SubCategoryPopup from '../SubCategoryPopup/SubCategoryPopup';
import AccountSettingsModal, { SETTINGS_TYPES } from '../AccountSettingsModal/AccountSettingsModal';
import FAQ from '../FAQ/FAQ';
import { buildFaqSearchEntries } from '../FAQ/faqData';
import { handleAction, navigateToAISearch } from '../../utils/searchActions';
import { useAiConsent } from '../Consent/AiConsentProvider';
import AISearchIcon from '../AISearch/AISearchIcon';
import { AI_SEARCH_PROMPT_CHIPS } from '../../constants/aiSearchPrompts';
import {
  tokenize,
  calculateTokenMatchScore,
  getPopularCategories,
  isNaturalLanguageQuery,
} from '../../utils/searchHelpers';
import { SEARCH_POSTS } from '../../services/apollo/queries/searchQueries';

const flattenCategories = (categories, parentKey = null, depth = 0) => {
  return categories.flatMap((category) => {
    const flattened = [{ ...category, parentKey, depth }];
    if (category.subcategories) {
      flattened.push(...flattenCategories(category.subcategories, category.key, depth + 1));
    }
    return flattened;
  });
};

// Calculate match score for an item against query tokens using fuzzy matching with stemming
const calculateMatchScore = (item, queryTokens, t, isShortQuery = false) => {
  if (queryTokens.length === 0) return 0;

  // Build searchable text from item properties
  const label = (item.label ? t(item.label) : item.title || '').toLowerCase();
  const key = (item.key || '').toLowerCase().replace(/-/g, ' ');
  const queryKeyword = (item.queryKeyword || '').toLowerCase();
  const description = (item.description || '').toLowerCase();
  
  // Combine all searchable text
  const searchableText = `${label} ${key} ${queryKeyword} ${description}`;
  const searchableTokens = tokenize(searchableText, { allowSingleChar: isShortQuery, filterStopWords: true });
  
  const matchRatio = calculateTokenMatchScore(queryTokens, searchableText, searchableTokens);
  const labelStartsBonus = label.startsWith(queryTokens[0]) ? 0.3 : 0;
  const depthPenalty = (item.depth || 0) * 0.05;
  const mainCategoryBonus = !item.parentKey ? 0.1 : 0;
  
  return matchRatio + labelStartsBonus + mainCategoryBonus - depthPenalty;
};

const PostTypeIcon = ({ type }) => {
  switch (type) {
    case 'lost':
      return <PawPrint className="w-4 h-4 text-red-500" />;
    case 'found':
      return <PawPrint className="w-4 h-4 text-green-500" />;
    case 'psa':
      return <Megaphone className="w-4 h-4 text-blue-500" />;
    case 'petService':
      return <Wrench className="w-4 h-4 text-purple-500" />;
    default:
      return <MapPin className="w-4 h-4 text-muted-foreground" />;
  }
};

const getPostTypeLabel = (type, t) => {
  switch (type) {
    case 'lost':
      return t('lost_pet', 'Lost Pet');
    case 'found':
      return t('found_pet', 'Found Pet');
    case 'psa':
      return t('announcement', 'Announcement');
    case 'petService':
      return t('pet_service', 'Pet Service');
    default:
      return t('post', 'Post');
  }
};

const useScrollHint = (rerunDep) => {
  const [showHint, setShowHint] = useState(false);
  const nodeRef = useRef(null);
  const observerRef = useRef(null);

  const evaluate = useCallback(() => {
    const el = nodeRef.current;
    if (!el) {
      setShowHint(false);
      return;
    }
    const SLACK = 4;
    const hasOverflow = el.scrollHeight - el.clientHeight > SLACK;
    const isNearBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= SLACK;
    setShowHint(hasOverflow && !isNearBottom);
  }, []);

  const setNode = useCallback(
    (node) => {
      const prev = nodeRef.current;
      if (prev) {
        prev.removeEventListener('scroll', evaluate);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      nodeRef.current = node;

      if (node) {
        node.addEventListener('scroll', evaluate, { passive: true });

        if (typeof ResizeObserver !== 'undefined') {
          observerRef.current = new ResizeObserver(evaluate);
          observerRef.current.observe(node);
          for (const child of Array.from(node.children)) {
            observerRef.current.observe(child);
          }
        }

        requestAnimationFrame(evaluate);
      } else {
        setShowHint(false);
      }
    },
    [evaluate]
  );

  const scrollDown = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    const delta = Math.max(el.clientHeight * 0.8, 80);
    el.scrollBy({ top: delta, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!nodeRef.current) return undefined;
    const rafId = requestAnimationFrame(evaluate);
    return () => cancelAnimationFrame(rafId);
  }, [evaluate, rerunDep]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  return { scrollRef: setNode, showHint, scrollDown };
};

const ScrollHintChevron = ({ visible, label, onClick }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        key="scroll-hint"
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.15 }}
        className="absolute bottom-1.5 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.button
          type="button"
          onClick={onClick}
          onMouseDown={(e) => e.preventDefault()}
          aria-label={label}
          animate={{ y: [0, 3, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          className="flex items-center justify-center w-6 h-6 rounded-full
                     bg-white/95 backdrop-blur-sm border border-border shadow-sm
                     hover:bg-white hover:shadow-md hover:scale-110 active:scale-95
                     transition-[background-color,box-shadow,transform] duration-150
                     focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer"
        >
          <ChevronDown className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
        </motion.button>
      </motion.div>
    )}
  </AnimatePresence>
);

const HeaderSearchPanel = ({
  isOpen,
  onClose,
  variant = 'dropdown',
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { ensureAiConsent } = useAiConsent();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [activeSubcategories, setActiveSubcategories] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [faqModalId, setFaqModalId] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const treatAsNL = useMemo(() => isNaturalLanguageQuery(query), [query]);
  
  // Post search state
  const [postResults, setPostResults] = useState([]);
  const searchTimeoutRef = useRef(null);
  
  // Get user location from Redux
  const userCoordinates = useSelector((state) => state.location.coordinates);
  const tempCoordinates = useSelector((state) => state.location.temporaryCoordinates);
  const effectiveCoordinates = tempCoordinates || userCoordinates;
  
  // Apollo lazy query for post search
  const [searchPosts, { loading: isSearchingPosts, data: searchData, error: searchError }] = useLazyQuery(SEARCH_POSTS, {
    fetchPolicy: 'network-only',
  });

  // Handle search results
  useEffect(() => {
    if (searchData?.searchPosts?.items) {
      setPostResults(searchData.searchPosts.items);
    }
  }, [searchData]);

  // Handle search errors
  useEffect(() => {
    if (searchError) {
      console.error('Post search error:', searchError);
      setPostResults([]);
    }
  }, [searchError]);
  
  const isInline = variant === 'inline';

  const flattenedCategories = useMemo(() => flattenCategories(mainCategories), []);

  const faqEntries = useMemo(() => buildFaqSearchEntries(t), [t]);

  // Calculate suggestions based on query
  const suggestions = useMemo(() => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return [];

    const isShortQuery = trimmedQuery.length <= 2;
    
    // Tokenize with appropriate settings for short queries
    const queryTokens = tokenize(trimmedQuery, { 
      allowSingleChar: isShortQuery, 
      filterStopWords: !isShortQuery 
    });
    
    if (queryTokens.length === 0) {
      return getPopularCategories(flattenedCategories, 5).map(cat => ({
        ...cat,
        type: 'category',
        score: 1,
        isFallback: true
      }));
    }

    const scoreThreshold = isShortQuery ? 0.3 : 0.4;

    const categoryResults = flattenedCategories
      .map(cat => ({
        ...cat,
        type: 'category',
        score: calculateMatchScore(cat, queryTokens, t, isShortQuery)
      }))
      .filter(cat => cat.score >= scoreThreshold);

    const expandedCardResults = searchExpandedCardData
      .map(item => ({
        ...item,
        type: 'expandedCard',
        score: calculateMatchScore(item, queryTokens, t, isShortQuery)
      }))
      .filter(item => item.score >= scoreThreshold);

    const faqResults = faqEntries
      .map(item => ({
        ...item,
        score: calculateMatchScore(item, queryTokens, t, isShortQuery)
      }))
      .filter(item => item.score >= scoreThreshold);

    // Combine and sort by score
    let allResults = [...categoryResults, ...expandedCardResults, ...faqResults]
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);

    if (allResults.length === 0 && isShortQuery) {
      allResults = getPopularCategories(flattenedCategories, 5).map(cat => ({
        ...cat,
        type: 'category',
        score: 1,
        isFallback: true
      }));
    }

    // Remove duplicates by key
    const uniqueResults = Array.from(
      new Map(allResults.map(item => [item.key || item.id, item])).values()
    );

    return uniqueResults;
  }, [query, flattenedCategories, faqEntries, t]);

  // Focus input when panel opens (dropdown variant only)
  useEffect(() => {
    if (!isInline && isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
    if (!isInline && !isOpen) {
      setQuery('');
    }
  }, [isOpen, isInline]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (isInline) {
          setIsFocused(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isInline]);

  useEffect(() => {
    if (!fullWidth || !isFocused) return;
    const initialScrollY = window.scrollY;

    const handleWindowScroll = () => {
      const scrollDelta = Math.abs(window.scrollY - initialScrollY);
      if (scrollDelta > 15) {
        inputRef.current?.blur();
        setIsFocused(false);
      }
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleWindowScroll, { passive: true });
    };
  }, [fullWidth, isFocused]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isInline) {
          setIsFocused(false);
          setQuery('');
          inputRef.current?.blur();
        } else if (isOpen) {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, isInline]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const trimmedQuery = query.trim();

    if (trimmedQuery.length < 2 || !effectiveCoordinates) {
      setPostResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchPosts({
        variables: {
          input: {
            query: trimmedQuery,
            userLocation: effectiveCoordinates,
            radiusInKm: 250,
            limit: 5
          }
        }
      });
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, effectiveCoordinates, searchPosts]);

  useEffect(() => {
    if (!isOpen && !isInline) {
      setPostResults([]);
    }
  }, [isOpen, isInline]);

  const handleFaqSelection = useCallback((faq) => {
    setQuery('');
    setPostResults([]);
    setIsFocused(false);
    inputRef.current?.blur();
    setFaqModalId(faq.faqId);
    onClose?.();
  }, [onClose]);

  const handleSelection = useCallback((selected) => {
    setQuery('');
    setPostResults([]);
    setIsFocused(false);
    inputRef.current?.blur();

    if (selected.type === 'faq') {
      handleFaqSelection(selected);
      return;
    }

    if (selected.type === 'expandedCard') {
      setSelectedGuide(selected);
      return;
    }

    // Handle category selection
    if (selected.subcategories) {
      handleAction(selected, {
        dispatch,
        navigate,
        setActiveSubcategories,
        setExpandedCard,
      });
    } else if (selected.action === 'navigate' && selected.route) {
      navigate(selected.route);
    } else if (selected.action === 'externalLink' && selected.url) {
      window.open(selected.url, '_blank');
    } else if (selected.action === 'query') {
      if (selected.parentKey) {
        navigate(`/search/${selected.parentKey}/${selected.key}`);
      } else {
        navigate(`/search/${selected.key}`);
      }
    } else if (selected.action === 'popup') {
      handleAction(selected, {
        dispatch,
        navigate,
        setActiveSubcategories,
        setExpandedCard,
      });
    }

    onClose();
  }, [dispatch, navigate, onClose, handleFaqSelection]);

  const handlePostSelection = useCallback((post) => {
    setQuery('');
    setPostResults([]);
    navigate(`/post/${post._id}`, {
      state: { postType: post.type },
    });
    onClose();
  }, [navigate, onClose]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleClearQuery = () => {
    setQuery('');
    setPostResults([]);
    inputRef.current?.focus();
  };

  const submitAIQuery = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      const consented = await ensureAiConsent();
      if (!consented) return;
      setQuery('');
      setPostResults([]);
      setIsFocused(false);
      onClose?.();
      navigateToAISearch(navigate, trimmed);
    },
    [navigate, onClose, ensureAiConsent]
  );

  const performSearch = () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    if (treatAsNL) {
      submitAIQuery(trimmed);
      return;
    }

    const topSuggestion = suggestions.find((s) => !s.isFallback);
    if (topSuggestion) {
      handleSelection(topSuggestion);
      return;
    }

    submitAIQuery(trimmed);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    performSearch();
  };

  // Get icon for category
  const getIcon = (iconKey) => {
    if (!iconKey) return null;
    const icon = iconMap[iconKey];
    if (icon) {
      return <span className="text-primary">{icon}</span>;
    }
    return null;
  };

  const isShowingFallback = suggestions.length > 0 && suggestions[0]?.isFallback;


  const renderAskAIRow = (trimmed) => {
    if (!trimmed || trimmed.length < 2 || isShowingFallback) return null;

    const promoted = treatAsNL;

    return (
      <button
        type="button"
        onClick={() => submitAIQuery(trimmed)}
        className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors border-b border-border ${
          promoted
            ? 'bg-primary/5 hover:bg-primary/10'
            : 'hover:bg-primary/5'
        }`}
        aria-label={`${t('ask_ai', 'Ask AI')}: ${trimmed}`}
      >
        <span
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            promoted
              ? 'bg-primary text-primary-foreground'
              : 'bg-primary/10 text-primary'
          }`}
        >
          <AISearchIcon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className={`text-[10px] font-semibold uppercase tracking-wide ${
                promoted ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {t('ask_ai', 'Ask AI')}
            </span>
            {promoted && (
              <span className="text-[9px] font-medium uppercase tracking-wide text-primary/70">
                {t('ai_suggested', 'Suggested')}
              </span>
            )}
          </div>
          <div className="text-sm text-foreground truncate">{trimmed}</div>
        </div>
      </button>
    );
  };

  const renderPromptChips = () => (
    <div>
      <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-muted-foreground uppercase sticky top-0">
        {t('try_a_prompt', 'Try asking AI')}
      </div>
      <div className="px-3 py-3 flex flex-wrap gap-2">
        {AI_SEARCH_PROMPT_CHIPS.map((chip) => (
          <button
            key={chip}
            type="button"
            onClick={() => submitAIQuery(chip)}
            className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700
                      hover:bg-gray-200 transition-colors border border-gray-200
                      focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  );

  const renderAiDisclosure = () => (
    <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200">
      <p className="text-[11px] leading-snug text-amber-900">
        {t(
          'ai_search_disclosure',
          'Powered by Google Gemini AI — responses are automated and animal-focused, not veterinary or legal advice.'
        )}{' '}
        <Link
          to="/terms"
          onClick={() => onClose?.()}
          className="font-semibold underline hover:no-underline whitespace-nowrap"
        >
          {t('how_our_ai_works', 'How our AI works →')}
        </Link>
      </p>
    </div>
  );

  // Render results content (shared between inline and dropdown variants)
  const renderResults = () => {
    const trimmed = query.trim();
    const hasQuery = trimmed.length > 0;

    const categorySuggestions = suggestions.filter((s) => s.type === 'category');
    const guideSuggestions = suggestions.filter((s) => s.type === 'expandedCard');
    const hasKeywordMatches =
      postResults.length > 0 ||
      categorySuggestions.length > 0 ||
      guideSuggestions.length > 0;

    if (!hasQuery) {
      const popularCategories = getPopularCategories(flattenedCategories, 5);
      return (
        <>
          {renderPromptChips()}

          {popularCategories.length > 0 && (
            <div className="border-t border-border">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-muted-foreground uppercase sticky top-0">
                {t('popular_categories', 'Popular Categories')}
              </div>
              {popularCategories.map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleSelection(item)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors
                           border-b border-border last:border-b-0 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {getIcon(item.iconKey)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-foreground truncate">
                      {t(item.label)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      );
    }

    const noKeywordHint =
      !hasKeywordMatches && !isSearchingPosts ? (
        <div className="px-4 py-4 text-center border-b border-border last:border-b-0">
          <p className="text-xs text-muted-foreground">
            {t(
              'no_keyword_matches',
              'No category matches. Press Enter or tap above to ask AI.'
            )}
          </p>
        </div>
      ) : null;

    return (
      <>
        {renderAskAIRow(trimmed)}

        {isShowingFallback && (
          <div className="px-4 py-3 bg-gray-50 border-b border-border">
            <p className="text-sm text-gray-500">
              {t('keep_typing', 'Keep typing for better results')}
            </p>
          </div>
        )}

        {noKeywordHint}

        {/* Community Posts Section */}
        {postResults.length > 0 && (
          <div className="border-b border-border">
            <div className="px-4 py-2 bg-blue-50 text-xs font-semibold text-blue-700 uppercase sticky top-0">
              {t('community_posts', 'Community Posts')}
            </div>
            {postResults.map((post) => (
              <button
                key={post._id}
                onClick={() => handlePostSelection(post)}
                className="w-full px-4 py-3 text-left hover:bg-blue-50/50 transition-colors 
                         border-b border-border last:border-b-0 flex items-center gap-3"
              >
                {post.animalImageUrl || post.imageUrl ? (
                  <img 
                    src={post.animalImageUrl || post.imageUrl} 
                    alt=""
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <PostTypeIcon type={post.type} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm text-foreground truncate">
                    {post.title || post.animalName || t('untitled', 'Untitled')}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <PostTypeIcon type={post.type} />
                    <span>{getPostTypeLabel(post.type, t)}</span>
                    {post.breed && (
                      <>
                        <span className="mx-1">•</span>
                        <span className="truncate">{post.breed}</span>
                      </>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Resources Section */}
        {suggestions.filter(s => s.type === 'category').length > 0 && (
          <div className="border-b border-border last:border-b-0">
            <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-muted-foreground uppercase sticky top-0">
              {isShowingFallback ? t('popular_categories', 'Popular Categories') : t('resources', 'Resources')}
            </div>
            {suggestions
              .filter(s => s.type === 'category')
              .map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleSelection(item)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors 
                           border-b border-border last:border-b-0 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {getIcon(item.iconKey)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-foreground truncate">
                      {t(item.label)}
                    </div>
                    {item.parentKey && (
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        in {t(flattenedCategories.find(c => c.key === item.parentKey)?.label || item.parentKey)}
                      </div>
                    )}
                  </div>
                  {item.subcategories && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {item.subcategories.length} options
                    </span>
                  )}
                </button>
              ))}
          </div>
        )}

        {/* Guides Section */}
        {suggestions.filter(s => s.type === 'expandedCard').length > 0 && (
          <div>
            <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-muted-foreground uppercase sticky top-0">
              {t('guides', 'Guides & Support')}
            </div>
            {suggestions
              .filter(s => s.type === 'expandedCard')
              .map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSelection(item)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors 
                           border-b border-border last:border-b-0"
                >
                  <div className="font-medium text-sm text-foreground">
                    {item.title}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                    {t(item.description)}
                  </div>
                </button>
              ))}
          </div>
        )}

        {/* FAQ & Help Section */}
        {suggestions.filter(s => s.type === 'faq').length > 0 && (
          <div>
            <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-muted-foreground uppercase sticky top-0">
              {t('faq_help', 'FAQ & Help')}
            </div>
            {suggestions
              .filter(s => s.type === 'faq')
              .map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleFaqSelection(item)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors 
                           border-b border-border last:border-b-0 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm text-foreground line-clamp-1">
                      {item.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      {item.description}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        )}
      </>
    );
  };

  const renderModals = () => {
    if (typeof document === 'undefined') return null;

    return createPortal(
      <>
        {/* Expanded Card Modal - for search expanded cards */}
        <AnimatePresence>
          {selectedGuide && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100]"
            >
              <ExpandedCard topic={selectedGuide} onClose={() => setSelectedGuide(null)} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Expanded Card Modal - for category expanded cards */}
        <AnimatePresence>
          {expandedCard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100]"
            >
              <ExpandedCard topic={expandedCard} onClose={() => setExpandedCard(null)} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Subcategory Popup */}
        <AnimatePresence>
          {activeSubcategories && (
            <SubCategoryPopup
              subcategories={activeSubcategories.subcategories}
              onClose={() => setActiveSubcategories(null)}
              dispatch={dispatch}
              navigate={navigate}
              setActiveSubcategories={setActiveSubcategories}
              setExpandedCard={setExpandedCard}
            />
          )}
        </AnimatePresence>

        {faqModalId !== null && (
          <AccountSettingsModal
            isOpen
            onClose={() => setFaqModalId(null)}
            type={SETTINGS_TYPES.FAQ}
          >
            <FAQ initialFaqId={faqModalId} />
          </AccountSettingsModal>
        )}
      </>,
      document.body
    );
  };

  const showResults = isInline ? isFocused : true;

  const scrollHintDep = `${query}|${suggestions.length}|${postResults.length}|${showResults}`;
  const inlineScroll = useScrollHint(scrollHintDep);
  const dropdownScroll = useScrollHint(scrollHintDep);

  // Inline variant
  if (isInline) {
    return (
      <>
        <div className={`relative ${fullWidth ? 'w-full' : ''}`} ref={dropdownRef}>
          <form onSubmit={handleFormSubmit} role="search">
            <div className="relative">
              <AISearchIcon
                size={fullWidth ? 20 : 20}
                className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors ${
                  treatAsNL ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
              <input
                ref={inputRef}
                type="search"
                enterKeyHint="search"
                inputMode="search"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck="false"
                autoComplete="off"
                placeholder={t(
                  'unified_search_placeholder',
                  'Search or ask anything about animals...'
                )}
                value={query}
                onChange={handleInputChange}
                onFocus={() => setIsFocused(true)}
                className={`${
                  fullWidth
                    ? `w-full pl-11 ${query ? 'pr-14' : 'pr-4'} py-2.5 sm:py-3`
                    : `w-[360px] xl:w-[440px] focus:w-[440px] xl:focus:w-[560px] pl-10 ${query ? 'pr-12' : 'pr-4'} py-2`
                } rounded-full border bg-background text-sm
                          focus:outline-none focus:ring-2 focus:border-transparent
                          placeholder:text-muted-foreground transition-all duration-200 truncate
                          [&::-webkit-search-cancel-button]:appearance-none ${
                            treatAsNL
                              ? 'border-primary/40 focus:ring-primary/40'
                              : 'border-input focus:ring-ring'
                          }`}
                aria-label={t('search_label', 'Search')}
              />

              <div
                className={`absolute ${
                  fullWidth ? 'right-2' : 'right-1.5'
                } top-1/2 -translate-y-1/2 flex items-center gap-1`}
              >
                {query && (
                  <button
                    type="button"
                    onClick={handleClearQuery}
                    aria-label={t('clear', 'Clear')}
                    className={`inline-flex items-center justify-center ${
                      fullWidth ? 'w-7 h-7' : 'w-6 h-6'
                    } rounded-full
                              text-muted-foreground hover:text-foreground hover:bg-muted transition-colors`}
                  >
                    <X
                      className={fullWidth ? 'w-4 h-4' : 'w-3.5 h-3.5'}
                      aria-hidden="true"
                    />
                  </button>
                )}
                {query.trim() && (
                  <button
                    type="submit"
                    aria-label={t('search', 'Search')}
                    className={`inline-flex items-center justify-center ${
                      fullWidth ? 'w-9 h-9' : 'w-7 h-7'
                    } rounded-full
                              bg-primary text-primary-foreground hover:bg-primary/90
                              transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40`}
                  >
                    <ArrowRight
                      className={fullWidth ? 'w-4 h-4' : 'w-3.5 h-3.5'}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* Results dropdown */}
          <AnimatePresence>
            {showResults && (
              <motion.div
                key="search-results-inline"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15 }}
                className={`absolute top-full mt-2 z-50 ${
                  fullWidth
                    ? 'left-0 right-0'
                    : 'right-0 w-[350px] xl:w-[400px]'
                }`}
              >
                <div
                  className={`bg-white rounded-xl shadow-xl overflow-hidden border-2 ${
                    treatAsNL ? 'border-primary/30' : 'border-border'
                  }`}
                >
                  {renderAiDisclosure()}
                  <div
                    ref={inlineScroll.scrollRef}
                    className="max-h-80 overflow-y-auto overscroll-contain"
                  >
                    {renderResults()}
                  </div>
                </div>
                <ScrollHintChevron
                  visible={inlineScroll.showHint}
                  label={t('scroll_for_more', 'Scroll for more options')}
                  onClick={inlineScroll.scrollDown}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {renderModals()}
      </>
    );
  }

  // Dropdown variant
  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="bg-white border-b border-border overflow-hidden"
          >
            <div className="px-3 sm:px-4 py-3" ref={dropdownRef}>
              <div
                className="h-px bg-border -mx-3 sm:-mx-4 -mt-3 mb-3"
                aria-hidden="true"
              />

              <div className="max-w-2xl mx-auto">
                <form onSubmit={handleFormSubmit} role="search">
                  <div className="relative">
                    <AISearchIcon
                      size={20}
                      className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors ${
                        treatAsNL ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    />
                    <input
                      ref={inputRef}
                      type="search"
                      enterKeyHint="search"
                      inputMode="search"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck="false"
                      autoComplete="off"
                      placeholder={t(
                        'unified_search_placeholder',
                        'Search or ask anything about animals...'
                      )}
                      value={query}
                      onChange={handleInputChange}
                      className={`w-full pl-11 ${query ? 'pr-12' : 'pr-4'} py-3 rounded-full border
                                bg-background text-sm focus:outline-none focus:ring-2 focus:border-transparent
                                placeholder:text-muted-foreground transition-colors truncate
                                [&::-webkit-search-cancel-button]:appearance-none ${
                                  treatAsNL
                                    ? 'border-primary/40 focus:ring-primary/40'
                                    : 'border-input focus:ring-ring'
                                }`}
                      aria-label={t('search_label', 'Search')}
                    />

                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                      {query && (
                        <button
                          type="button"
                          onClick={handleClearQuery}
                          aria-label={t('clear', 'Clear')}
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full
                                    text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                          <X className="w-4 h-4" aria-hidden="true" />
                        </button>
                      )}
                      {query.trim() && (
                        <button
                          type="submit"
                          aria-label={t('search', 'Search')}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-full
                                    bg-primary text-primary-foreground hover:bg-primary/90
                                    transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
                        >
                          <ArrowRight className="w-4 h-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>

              {/* Search Results Dropdown */}
              <AnimatePresence>
                {showResults && (
                  <motion.div
                    key="search-results-dropdown"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="relative mt-2 max-w-2xl mx-auto"
                  >
                    <div
                      className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 ${
                        treatAsNL ? 'border-primary/30' : 'border-border'
                      }`}
                    >
                      {renderAiDisclosure()}
                      <div
                        ref={dropdownScroll.scrollRef}
                        className="max-h-80 overflow-y-auto overscroll-contain"
                      >
                        {renderResults()}
                      </div>
                    </div>
                    <ScrollHintChevron
                      visible={dropdownScroll.showHint}
                      label={t('scroll_for_more', 'Scroll for more options')}
                      onClick={dropdownScroll.scrollDown}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {renderModals()}
    </>
  );
};

export default HeaderSearchPanel;

